/**
 * StrikeZone Component
 */

import React, { ReactElement, FC, useRef } from 'react';
import { withTheme } from 'styled-components';
import useControlledState from '../hooks/useControlledState/useControlledState';
import bindHandlers from '../../utils/handlerHelper';
import Handler from '../../commonTypes/handler.types';
import {
    StrikeZoneProps,
    StrikeZoneStyleProps,
    StrikeZoneSelectedProps,
} from './strikeZone.types';
import {
    StrikeZoneSvg,
    StrikeZoneG,
    StrikeZonePath,
} from './strikeZone.style';
import {
    viewBoxMinX,
    viewBoxMinY,
    viewBoxWidth,
    viewBoxHeight,
    zoneWidth,
    zoneHeight,
    batterZoneMatrix,
    pitcherZoneMatrix,
    outlierWidth,
    outlierHeight,
    batterOutlierMatrix,
    pitcherOutlierMatrix,
    homePlateWidth,
    homePlateHeight,
    strikeZoneDataValueKey,
    strikeZoneDeselectedEvent,
    strikeZoneSelectedEvent,
    keyCodes,
    zoneAltText,
    outlierAltText,
} from './constants';
import {
    generateStyles,
    renderRectMatrix,
    buildStateObject,
} from './utils';

const StrikeZone: FC<StrikeZoneProps> = ({
    width = '100%',
    pitcherView = false,
    altText = `StrikeZone`,
    theme = {},
    handlers = {},
    selectable = false,
    showLabel = false,
    defaultSelected,
    selected,
    className,
}: StrikeZoneProps) => {

    const svgRef = useRef<HTMLElement | null>(null);
    const focusedTarget = useRef<HTMLElement | null>(null);
    const [selectedState, setSelectedState] = useControlledState({
        controlled: buildStateObject(selected),
        default: buildStateObject(defaultSelected),
        name: 'StrikeZone',
    });

    const {
        onClick,
        onChange,
        ...otherHandlers
    } = handlers;

    const internalClickHandler = (event: React.MouseEvent | React.KeyboardEvent) => {
        if(onClick) {
            onClick(event);
        }
        if(!selectable) {
            return;
        }
        let target: HTMLElement | null = event.target as HTMLElement;
        if(event.type === 'click') {
            target = target.parentElement;
        } else if(event.type === 'keydown' && focusedTarget.current) {
            target = focusedTarget.current;
        }
        const selectedValue: string | null | undefined = target?.getAttribute(strikeZoneDataValueKey);
        if(!selectedValue) {
            return;
        }
        focusedTarget.current = target;
        const isSelected: boolean = selectedState ? selectedState[selectedValue] : false;
        const newSelectedMap: object = {
            ...selectedState,
        };
        if(isSelected) {
            delete newSelectedMap[selectedValue];
        } else {
            newSelectedMap[selectedValue] = true;
            if(svgRef.current) {
                svgRef.current.focus();
            }
        }
        setSelectedState(newSelectedMap);
        if (onChange) {
            const newSelected: Array<string> = Object.keys(newSelectedMap);
            const changes: StrikeZoneSelectedProps = {
                label: selectedValue,
                value: selectedValue,
            };
            onChange({
                selected: newSelected.map((value) => {
                    const selectedProps: StrikeZoneSelectedProps = {
                        label: value,
                        value,
                    };
                    return selectedProps;
                }),
                changes,
                event: isSelected ? strikeZoneDeselectedEvent : strikeZoneSelectedEvent,
            });
        }
    };

    const internalHandlers: Handler = {
        onClick: internalClickHandler,
        onKeyDown: (event: React.KeyboardEvent) => {
            const { keyCode }: any = event;
            const {
                space,
                enter,
                tab,
            }: any = keyCodes;
            if(keyCode === enter || keyCode === space) {
                if(focusedTarget.current) {
                    focusedTarget.current.focus();
                }
                internalClickHandler(event);
            } else if(keyCode === tab) {
                if(focusedTarget.current) {
                    focusedTarget.current.focus();
                    focusedTarget.current = null;
                }
            }
        },
    };
    const combinedHandlers: Handler = bindHandlers(
        null,
        otherHandlers,
        internalHandlers,
    );

    const strikeZoneStyles: StrikeZoneStyleProps = generateStyles(typeof theme?.dark !== 'undefined');

    const commonRenderRectMatrixProps: any = {
        viewBoxWidth,
        viewBoxHeight,
        selected: selectedState,
        styles: strikeZoneStyles,
        selectable,
        showLabel,
    };

    const zones : ReactElement = renderRectMatrix({
        rectMatrix: pitcherView ? pitcherZoneMatrix : batterZoneMatrix,
        width: zoneWidth,
        height: zoneHeight,
        ariaLabel: `${altText} ${zoneAltText}`,
        ...commonRenderRectMatrixProps,
    });

    const outliers : ReactElement = renderRectMatrix({
        rectMatrix: pitcherView ? pitcherOutlierMatrix : batterOutlierMatrix,
        width: outlierWidth,
        height: outlierHeight,
        ariaLabel: `${altText} ${outlierAltText}`,
        ...commonRenderRectMatrixProps,
    });

    return (
        <StrikeZoneSvg
            ref={svgRef}
            className={className}
            viewBox={`${viewBoxMinX} ${viewBoxMinY} ${viewBoxWidth} ${viewBoxHeight}`}
            xmlns={'http://www.w3.org/2000/svg'}
            role={!selectable ? 'img' : undefined}
            tabIndex={selectable ? 0 : -1}
            {...combinedHandlers}
            $width={width}
            $selectable={selectable}
        >
            <desc>{`${altText} ${pitcherView ? 'Pitcher' : 'Batter'} Point Of View`}</desc>
            {outliers}
            {zones}
            <StrikeZoneG
                $transform={'translate(0px, 19px)'}
            >
                <StrikeZonePath
                    d={'M29 299L181 299 181 314 104 327 29 314z'}
                    $transform={pitcherView ? 'matrix(1, 0, 0, -1, 0, 626)' : undefined}
                    $fillColor={strikeZoneStyles.homePlateFillColor}
                    $width={homePlateWidth}
                    $height={homePlateHeight}
                />
            </StrikeZoneG>
        </StrikeZoneSvg>
    );
};

export default withTheme(StrikeZone);
